// The birth-lunar-day slice of the "Kas šī par dienu" engine, ported from
// docs/what-is-the-day-plan/meness-astro.mjs. Dependency-free. Runs in the browser
// (birth_day_controller.js) and in Node (spec/lib/pirts_day/lunar_day_js_spec.rb
// cross-checks it against PirtsDay::Almanac).
//
// Only the lunar-day-number calc is carried over — that is all the birth feature needs.
// A birth date turns into a number 1..30 on the device; only the number is ever sent
// to the server.

const RAD = Math.PI / 180;
const n360 = (x) => ((x % 360) + 360) % 360;
const H0 = -0.817; // moon: upper limb touching horizon, refraction included

// Ported from PirtsDay::Almanac::SITES (app/services/pirts_day/almanac.rb) — just the
// lat/lon this module needs, keyed the same way.
const SITES = {
  riga: { lat: 56.9496, lon: 24.1052 },
  daugavpils: { lat: 55.8747, lon: 26.5362 },
  liepaja: { lat: 56.5047, lon: 21.0108 },
  jelgava: { lat: 56.65, lon: 23.7128 },
  jurmala: { lat: 56.968, lon: 23.7704 },
  ventspils: { lat: 57.3894, lon: 21.5606 },
  rezekne: { lat: 56.51, lon: 27.33 },
  valmiera: { lat: 57.5386, lon: 25.4261 },
  jekabpils: { lat: 56.4986, lon: 25.8578 },
  ogre: { lat: 56.8167, lon: 24.6047 },
  tukums: { lat: 56.9672, lon: 23.1547 },
  cesis: { lat: 57.3119, lon: 25.2747 },
  salaspils: { lat: 56.8608, lon: 24.3494 },
  kuldiga: { lat: 56.9678, lon: 21.9614 },
  saldus: { lat: 56.6647, lon: 22.4939 },
  talsi: { lat: 57.2447, lon: 22.5872 },
  dobele: { lat: 56.625, lon: 23.2814 },
  kraslava: { lat: 55.895, lon: 27.1656 },
  gulbene: { lat: 57.1783, lon: 26.7561 },
};

export const toJD = (date) => date.getTime() / 86400000 + 2440587.5;

function sunLon(jd) {
  const T = (jd - 2451545) / 36525;
  const L0 = 280.46646 + 36000.76983 * T + 0.0003032 * T * T;
  const M = 357.52911 + 35999.05029 * T - 0.0001537 * T * T;
  const C1 =
    (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(M * RAD) +
    (0.019993 - 0.000101 * T) * Math.sin(2 * M * RAD) +
    0.000289 * Math.sin(3 * M * RAD);
  return n360(L0 + C1);
}

// Schlyter's lunar theory — ~2' accuracy, enough for the day boundary.
function moonEcl(jd) {
  const d = jd - 2451543.5;
  const N = n360(125.1228 - 0.0529538083 * d);
  const inc = 5.1454;
  const w = n360(318.0634 + 0.1643573223 * d);
  const a = 60.2666;
  const e = 0.0549;
  const M = n360(115.3654 + 13.0649929509 * d);
  const Ms = n360(356.047 + 0.9856002585 * d);
  const ws = n360(282.9404 + 4.70935e-5 * d);
  const Ls = n360(ws + Ms);
  const Lm = n360(N + w + M);
  const D = n360(Lm - Ls);
  const Fa = n360(Lm - N);

  let E = M + (180 / Math.PI) * e * Math.sin(M * RAD) * (1 + e * Math.cos(M * RAD));
  for (let k = 0; k < 6; k++) {
    E -= (E - (180 / Math.PI) * e * Math.sin(E * RAD) - M) / (1 - e * Math.cos(E * RAD));
  }
  const x = a * (Math.cos(E * RAD) - e);
  const y = a * Math.sqrt(1 - e * e) * Math.sin(E * RAD);
  const r0 = Math.hypot(x, y);
  const v = n360(Math.atan2(y, x) / RAD);

  const cvw = Math.cos((v + w) * RAD);
  const svw = Math.sin((v + w) * RAD);
  const cN = Math.cos(N * RAD);
  const sN = Math.sin(N * RAD);
  const ci = Math.cos(inc * RAD);
  const si = Math.sin(inc * RAD);
  const xe = r0 * (cN * cvw - sN * svw * ci);
  const ye = r0 * (sN * cvw + cN * svw * ci);
  const ze = r0 * svw * si;

  let lon = n360(Math.atan2(ye, xe) / RAD);
  let lat = Math.atan2(ze, Math.hypot(xe, ye)) / RAD;
  let r = Math.sqrt(xe * xe + ye * ye + ze * ze);

  lon +=
    -1.274 * Math.sin((M - 2 * D) * RAD) +
    0.658 * Math.sin(2 * D * RAD) -
    0.186 * Math.sin(Ms * RAD) -
    0.059 * Math.sin((2 * M - 2 * D) * RAD) -
    0.057 * Math.sin((M - 2 * D + Ms) * RAD) +
    0.053 * Math.sin((M + 2 * D) * RAD) +
    0.046 * Math.sin((2 * D - Ms) * RAD) +
    0.041 * Math.sin((M - Ms) * RAD) -
    0.035 * Math.sin(D * RAD) -
    0.031 * Math.sin((M + Ms) * RAD) -
    0.015 * Math.sin((2 * Fa - 2 * D) * RAD) +
    0.011 * Math.sin((M - 4 * D) * RAD);
  lat +=
    -0.173 * Math.sin((Fa - 2 * D) * RAD) -
    0.055 * Math.sin((M - Fa - 2 * D) * RAD) -
    0.046 * Math.sin((M + Fa - 2 * D) * RAD) +
    0.033 * Math.sin((Fa + 2 * D) * RAD) +
    0.017 * Math.sin((2 * M + Fa) * RAD);
  r += -0.58 * Math.cos((M - 2 * D) * RAD) - 0.46 * Math.cos(2 * D * RAD);

  return { lon: n360(lon), lat, r };
}

function eclToEq(lon, lat, jd) {
  const T = (jd - 2451545) / 36525;
  const ob = (23.4392911 - 0.0130042 * T) * RAD;
  const l = lon * RAD;
  const b = lat * RAD;
  const x = Math.cos(b) * Math.cos(l);
  const y = Math.cos(b) * Math.sin(l) * Math.cos(ob) - Math.sin(b) * Math.sin(ob);
  const z = Math.cos(b) * Math.sin(l) * Math.sin(ob) + Math.sin(b) * Math.cos(ob);
  return { ra: n360(Math.atan2(y, x) / RAD), dec: Math.atan2(z, Math.hypot(x, y)) / RAD };
}

const gmst = (jd) => {
  const T = (jd - 2451545) / 36525;
  return n360(280.46061837 + 360.98564736629 * (jd - 2451545) + 0.000387933 * T * T);
};

function moonAlt(jd, { lat, lon }) {
  const m = moonEcl(jd);
  const { ra, dec } = eclToEq(m.lon, m.lat, jd);
  let ha = n360(gmst(jd) + lon - ra);
  if (ha > 180) ha -= 360;
  const sinAlt =
    Math.sin(dec * RAD) * Math.sin(lat * RAD) +
    Math.cos(dec * RAD) * Math.cos(lat * RAD) * Math.cos(ha * RAD);
  const alt = Math.asin(sinAlt) / RAD;
  const par = Math.asin(1 / m.r) / RAD;
  return alt - par * Math.cos(alt * RAD);
}

function riseBetween(t0, t1, site) {
  let a = t0;
  let b = t1;
  for (let i = 0; i < 40; i++) {
    const m = (a + b) / 2;
    if (moonAlt(m, site) - H0 < 0) a = m;
    else b = m;
  }
  return (a + b) / 2;
}

const signedElong = (jd) => {
  const e = n360(moonEcl(jd).lon - sunLon(jd));
  return e > 180 ? e - 360 : e;
};

function lastNewMoon(jd) {
  const step = 0.25;
  let t1 = jd;
  let f1 = signedElong(t1);
  for (let k = 0; k < 170; k++) {
    const t0 = t1 - step;
    const f0 = signedElong(t0);
    if (f0 < 0 && f1 >= 0 && f1 - f0 < 30) {
      let a = t0;
      let b = t1;
      for (let i = 0; i < 45; i++) {
        const m = (a + b) / 2;
        if (signedElong(m) < 0) a = m;
        else b = m;
      }
      return (a + b) / 2;
    }
    t1 = t0;
    f1 = f0;
  }
  return jd - 29.53;
}

function lunarDayAt(jd, site) {
  const nm = lastNewMoon(jd);
  const step = 20 / 1440;
  let count = 0;
  let prev = moonAlt(nm, site) - H0;
  for (let t = nm + step; t < nm + 31; t += step) {
    const cur = moonAlt(t, site) - H0;
    if (prev < 0 && cur >= 0) {
      const r = riseBetween(t - step, t, site);
      if (r <= jd) count++;
      else break;
    }
    prev = cur;
  }
  return count + 1;
}

// Rīga is UTC+2 Oct–Mar, UTC+3 Apr–Sep. The DST edges are within the engine's ~arcmin
// tolerance and never move the day number (see the boundary cross-check in
// spec/lib/pirts_day/lunar_day_js_spec.rb) — a fixed rule keeps this dependency-free
// rather than pulling in a full IANA tz database for the browser.
const rigaUtcOffsetHours = (month) => (month >= 4 && month <= 9 ? 3 : 2);

// The lunar day (1..30) for a birth date and (optional) time, computed as Rīga-local
// wall time at the given site. `isoDate` is a plain "YYYY-MM-DD" string; `time` a
// "HH:MM" string (blank/invalid defaults to noon); `siteKey` a PirtsDay::Almanac::SITES
// key (unknown/absent defaults to "riga"). Nothing about the date/time is kept —
// only the resulting day number ever leaves the browser.
export function birthLunarDay(isoDate, time = "12:00", siteKey = "riga") {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return null;

  const [hh, mm] = /^\d{1,2}:\d{2}$/.test(time) ? time.split(":").map(Number) : [12, 0];
  const [h, min] = hh < 24 && mm < 60 ? [hh, mm] : [12, 0];

  const offset = rigaUtcOffsetHours(m);
  const jd = toJD(new Date(Date.UTC(y, m - 1, d, h - offset, min)));
  const site = SITES[siteKey] ?? SITES.riga;
  return lunarDayAt(jd, site);
}
